/**
 * What size of screen a design is drawn for, and what changing it does.
 *
 * A design is drawn in canvas units and scaled to whatever panel shows it, keeping
 * its shape. A design of the wrong shape therefore does not break; it shrinks, and
 * leaves bands of nothing beside it. The physical kiosk proved the cost on 30
 * September 2026: a 1080×1920 design on its 1024×1280 panel was drawn 720 pixels
 * wide with 150 empty on each side. So the size is the designer's to choose, from
 * the panels branches actually have.
 */

import { clamp } from './editing'
import type { LayoutDocument, LayoutKind } from './types'
import { t, type StaffKey, type StaffLanguage } from '../lib/staffText'

export interface CanvasPreset {
  width: number
  height: number
  /** The panel's name, as a phrase key; presetLabel adds its size. */
  label: StaffKey
}

const KIOSK: CanvasPreset[] = [
  { width: 1024, height: 1280, label: 'designs.size.kiosk' },
  { width: 1080, height: 1920, label: 'designs.size.portraitFullHD' },
  { width: 768, height: 1366, label: 'designs.size.portrait' },
  { width: 1280, height: 1024, label: 'designs.size.landscape' },
  { width: 1920, height: 1080, label: 'designs.size.landscapeFullHD' },
]

const DISPLAY: CanvasPreset[] = [
  { width: 1920, height: 1080, label: 'designs.size.fullHD' },
  { width: 1366, height: 768, label: 'designs.size.hd' },
  { width: 1280, height: 720, label: 'designs.size.hd' },
  { width: 1080, height: 1920, label: 'designs.size.portrait' },
]

// A small panel above one desk: landscape first, then the same panels turned.
// Small, Wide and Bar are the Studio prototype's panels (internal/layout CounterSizes).
const COUNTER: CanvasPreset[] = [
  { width: 1920, height: 1080, label: 'designs.size.landscapeFullHD' },
  { width: 1280, height: 800, label: 'designs.size.landscape' },
  { width: 1024, height: 600, label: 'designs.size.landscape' },
  { width: 800, height: 480, label: 'designs.size.small' },
  { width: 1280, height: 400, label: 'designs.size.wide' },
  { width: 1920, height: 540, label: 'designs.size.bar' },
  { width: 1080, height: 1920, label: 'designs.size.portraitFullHD' },
  { width: 800, height: 1280, label: 'designs.size.portrait' },
  { width: 600, height: 1024, label: 'designs.size.portrait' },
  { width: 480, height: 800, label: 'designs.size.portrait' },
]

/** "Portrait 1080 × 1920", in the reader's language. */
export function presetLabel(p: CanvasPreset, lang: StaffLanguage): string {
  return `${t(p.label, lang)} ${p.width} × ${p.height}`
}

/** The bounds the server holds a typed size to (internal/layout). */
export const MIN_SCREEN_SIDE = 320
export const MAX_SCREEN_SIDE = 3840

/** The sizes offered for a kind of design, the most likely first. Paper has none:
 *  its width is the printer's. */
export function presetsFor(kind: LayoutKind): CanvasPreset[] {
  if (kind === 'kiosk') return KIOSK
  if (kind === 'display') return DISPLAY
  if (kind === 'counter') return COUNTER
  return []
}

/**
 * The same design on a canvas of another size.
 *
 * Positions and sizes are scaled by the two ratios separately, so that what was a
 * margin stays a margin and what filled the width still fills it. A square button
 * becomes a slightly different rectangle, which is what a designer moving a design
 * to another panel expects to tidy; a design that ran off the edge, or sat in the
 * middle of the old shape, is not. Text is not touched: it sizes itself from its
 * box.
 */
export function resizeCanvas(doc: LayoutDocument, width: number, height: number): LayoutDocument {
  if (width === doc.canvas.width && height === doc.canvas.height) return doc
  const sx = width / doc.canvas.width
  const sy = height / doc.canvas.height
  const canvas = { ...doc.canvas, width, height }
  return {
    ...doc,
    canvas,
    elements: doc.elements.map((el) =>
      clamp(
        {
          ...el,
          x: Math.round(el.x * sx),
          y: Math.round(el.y * sy),
          w: Math.round(el.w * sx),
          h: Math.round(el.h * sy),
        },
        canvas,
      ),
    ),
  }
}

/** A start drawn for the screen chosen in New: every start, not only the made ones. */
export function atSize(doc: LayoutDocument, size: { width: number; height: number }): LayoutDocument {
  if (doc.canvas.width === size.width && doc.canvas.height === size.height) return doc
  return resizeCanvas(doc, size.width, size.height)
}
